package com.siriusxm.cmc.views

import androidx.compose.runtime.*
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.SVGIcons
import com.siriusxm.pia.components.svgIcon
import com.siriusxm.pia.components.tabView
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * A unified search panel with two modes: Search and Discovery.
 * Search supports smart autocomplete for type: and visibility: filters.
 * Discovery functionality is planned for future implementation.
 *
 * The [initialQuery], [initialTypes], and [initialVisibility] parameters are forwarded to the
 * Search tab's [SmartSearchInput] so a search carried in the URL pre-fills the field and its
 * filter chips. They default to an empty search for callers (like the main home page) that
 * open the panel with no active query.
 *
 * @param initialQuery free-text query to pre-fill the Search input with
 * @param initialTypes entity-type ids to pre-populate as removable filter chips
 * @param initialVisibility visibility filter to pre-populate (true = visible, false = hidden,
 *   null = no filter)
 */
@Composable
fun UberSearch(
    initialQuery: String = "",
    initialTypes: List<String> = emptyList(),
    initialVisibility: Boolean? = null
) {
    var discoveryQuery by remember { mutableStateOf("") }

    tabView {
        tab("Search", "search") {
            Div({ style { marginTop(16.px) } }) {
                SmartSearchInput(initialQuery, initialTypes, initialVisibility)
            }
        }

//        tab("Discovery", "discovery") {
//            PreviewBanner()
//            SearchInputRow(
//                value = discoveryQuery,
//                placeholder = "Ask a question...",
//                onValueChange = { discoveryQuery = it },
//                onExecute = {
//                    if (discoveryQuery.isNotBlank()) {
//                        Application.navigate("discovery/conversation?q=${encodeURIComponent(discoveryQuery)}")
//                    }
//                }
//            )
//            ConversationExamples { example ->
//                discoveryQuery = example
//            }
//        }
    }
}

/**
 * Displays example conversation queries that users can click to populate the input.
 *
 * @param onExampleClick Callback when an example is clicked, receives the example text
 */
@Composable
private fun ConversationExamples(onExampleClick: (String) -> Unit) {
    val examples = listOf(
        "What channels were updated in the past 3 days?",
        "Show me some podcast episodes that talked about Portugal"
    )

    Div({
        style {
            marginTop(24.px)
        }
    }) {
        Div({
            style {
                color(SXMUI.subtleTextColor.value())
                fontSize(13.px)
                marginBottom(12.px)
            }
        }) {
            Text("Try an example:")
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(8.px)
            }
        }) {
            examples.forEach { example ->
                Div({
                    style {
                        padding(12.px, 16.px)
                        backgroundColor(SXMUI.containerHeaderBackgroundColor.value())
                        borderRadius(8.px)
                        cursor("pointer")
                        fontSize(14.px)
                    }
                    onClick { onExampleClick(example) }
                }) {
                    Text(example)
                }
            }
        }
    }
}

/**
 * A banner indicating that the Discovery feature is in preview mode.
 * Warns users that this feature should not be relied upon for mission-critical data.
 */
@Composable
private fun PreviewBanner() {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            marginTop(16.px)
            padding(12.px, 16.px)
            backgroundColor(Color("#FFF3CD"))
            border(1.px, LineStyle.Solid, Color("#FFECB5"))
            borderRadius(6.px)
        }
    }) {
        Span({
            style {
                fontWeight("bold")
                color(Color("#856404"))
                fontSize(12.px)
                padding(2.px, 8.px)
                backgroundColor(Color("#FFE69C"))
                borderRadius(4.px)
            }
        }) {
            Text("PREVIEW")
        }
        Span({
            style {
                color(Color("#664D03"))
                fontSize(14.px)
            }
        }) {
            Text("This feature is in preview. Do not rely on it for mission-critical or accurate data.")
        }
    }
}

/**
 * A reusable row containing a text input and an execute button with a blocky arrow icon.
 * Used within the UberSearch tabs for both Search and Discovery modes.
 *
 * @param value The current value of the text input
 * @param placeholder Placeholder text shown when the input is empty
 * @param onValueChange Callback when the input value changes
 * @param onExecute Callback when the execute button is clicked
 */
@Composable
private fun SearchInputRow(
    value: String,
    placeholder: String,
    onValueChange: (String) -> Unit,
    onExecute: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
            marginTop(16.px)
        }
    }) {
        Input(InputType.Text) {
            style {
                flexGrow(1)
                padding(10.px, 14.px)
                fontSize(14.px)
                border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
                borderRadius(SXMUI.boxRadius.value())
                property("outline", "none")
            }
            placeholder(placeholder)
            value(value)
            onInput { onValueChange(it.value) }
            onKeyDown { event ->
                if (event.key == "Enter") {
                    onExecute()
                }
            }
        }

        Button({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                width(40.px)
                height(40.px)
                padding(0.px)
                backgroundColor(SXMUI.buttonBackgroundPrimary.value())
                border(0.px)
                borderRadius(50.percent)
                cursor("pointer")
                color(SXMUI.buttonForegroundPrimary.value())
            }
            onClick { onExecute() }
        }) {
            svgIcon(SVGIcons.arrowRight) {
                size = 20.px
            }
        }
    }
}